import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  Download,
  ExternalLink,
  Gamepad2,
  Sparkles,
  Swords,
  Target,
  Trophy,
} from 'lucide-react';
import { useState } from 'react';
import { GoogleSignInButton } from '@/components/auth/google-sign-in-button';
import { NewsCarousel } from '@/components/news/news-carousel';
import { SocialLinks } from '@/components/social/social-links';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/auth-context';
import { useGame } from '@/contexts/game-context';
import { resolveCampaignLanding } from '@/lib/campaign-landing';
import { useDownloadTarget } from '@/lib/use-download-target';
import { getVisibleNewsOptions } from '@api/generated/@tanstack/react-query.gen';

export const Route = createFileRoute('/')({
  // Read the campaign code from the URL so a campaign can route visitors to a
  // custom landing page.
  validateSearch: (
    search: Record<string, unknown>,
  ): { ref?: string; utm_campaign?: string } => ({
    ref: typeof search.ref === 'string' ? search.ref : undefined,
    utm_campaign:
      typeof search.utm_campaign === 'string' ? search.utm_campaign : undefined,
  }),
  // Redirect at the router level (before render, no home-page flash) when the
  // campaign has a configured landing page. Attribution is already captured by
  // captureAttributionFromUrl() in main.tsx, so it survives the redirect.
  beforeLoad: ({ search }) => {
    const landing = resolveCampaignLanding(search);
    if (landing) throw redirect({ to: landing });
  },
  component: HomePage,
});

/**
 * Public landing page. Mirrors the look of the Mainstage app-store listing
 * — big hero with the app icon, name, tagline, and a single dominant
 * DOWNLOAD button — while still doing webapp duties (sign-in + news).
 *
 * Mobile-first: the layout stacks vertically and the CTA is full-width.
 * From `sm:` upward we shift to a side-by-side hero with a fixed natural
 * width for the CTA cluster.
 *
 * The download target swaps based on user agent (see useDownloadTarget):
 * desktop visitors get the direct Windows installer, mobile visitors get
 * the app-store landing page so they can come back from a real computer.
 */
function HomePage() {
  const { isAuthenticated } = useAuth();
  // Landing copy names the active game rather than hardcoding one.
  const { activeGameName } = useGame();
  const [signInError, setSignInError] = useState<string | null>(null);
  const download = useDownloadTarget();

  // Public endpoint — safe to fetch for signed-out visitors too.
  const { data: newsData, isLoading: newsLoading } = useQuery(
    getVisibleNewsOptions(),
  );
  const newsItems = newsData ?? [];

  return (
    <div className="relative overflow-hidden">
      {/* Hero background art + ambient glows. The webp is a dark crowd
          image with a green wash that matches the in-app palette; we
          fade it out with a gradient so text on top stays legible. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[520px] sm:h-[640px]">
        <div
          className="absolute inset-0 bg-cover bg-top opacity-40"
          style={{ backgroundImage: 'url(/hero-background.webp)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/70 to-background" />
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute top-40 -right-32 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
      </div>

      <div
        className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 sm:pt-14 pb-16 sm:pb-20 space-y-12 sm:space-y-16"
        // Mobile browsers (Chrome's bottom tab bar, iOS Safari's bottom
        // chrome) overlap the viewport. Without this inset, the final
        // section sits flush against — or behind — the browser UI and
        // looks like the page is cut off.
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 5rem)' }}
      >
        {/* HERO ------------------------------------------------------- */}
        <section className="space-y-6 sm:space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-7 text-center sm:text-left">
            <img
              src="/mainstage-icon.png"
              alt="Mainstage app icon"
              className="h-20 w-20 sm:h-28 sm:w-28 rounded-2xl border border-border/60 bg-card/60 shadow-lg shadow-black/30 mx-auto sm:mx-0 shrink-0"
            />
            <div className="space-y-2 sm:space-y-3 min-w-0">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border/60 bg-card/60 backdrop-blur-sm text-xs text-muted-foreground">
                <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                {activeGameName} Companion · Beta
              </div>
              {/* Solid foreground color (no bg-clip-text). The gradient
                  mask was clipping the "g" descender — browsers compute
                  the text-clip mask tight against the glyph and ignore
                  leading/padding, so the only reliable fix is to drop
                  the gradient. */}
              <h1 className="text-4xl sm:text-6xl font-bold tracking-tight leading-[1.1] pb-1 text-foreground">
                Mainstage
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                By <span className="font-medium text-foreground/80">Mainstage Labs</span>
                <span className="mx-2 text-foreground/30">·</span>
                Free · Contains Ads
              </p>
            </div>
          </div>

          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto sm:mx-0 text-center sm:text-left">
            A full {activeGameName} companion with on-screen combo practice,
            notes, frame data, matchup tools, tier lists, and leaderboards.
            Everything you need to level up your game fast.
          </p>

          {/* Primary CTA cluster. Both buttons share h-12 + full-width on
              mobile so they read as one stacked group; on desktop they
              shrink to natural width and sit side-by-side. */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
            <Button
              asChild
              size="lg"
              className="h-12 px-8 text-base font-semibold w-full sm:w-auto shadow-lg shadow-primary/20"
            >
              <a
                href={download.href}
                rel="noopener noreferrer"
                // External installer / store link — open in the same tab on
                // desktop (the .exe download fires and the page stays put),
                // new tab on mobile so the user keeps our landing page.
                target={download.isMobile ? '_blank' : undefined}
              >
                {download.isMobile ? (
                  <ExternalLink className="h-5 w-5" />
                ) : (
                  <Download className="h-5 w-5" />
                )}
                {download.label}
              </a>
            </Button>

            {/* Secondary: sign-in for logged-out visitors. The download is the
                goal of this page; signed-in users just see the download. */}
            {!isAuthenticated && (
              <div className="w-full sm:w-[260px] flex flex-col gap-1">
                <GoogleSignInButton size="lg" onError={setSignInError} />
                {signInError && (
                  <p
                    role="alert"
                    className="text-xs text-destructive max-w-sm"
                  >
                    {signInError}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Social row under the CTA — second-best conversion path for
              someone who didn't click Download. Caption + icons sit
              directly below so the visual funnel reads: Download → Join. */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 pt-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground text-center sm:text-left">
              Join the community
            </span>
            <SocialLinks variant="subtle" className="justify-center sm:justify-start" />
          </div>

          {!download.isMobile && (
            <p className="text-xs text-muted-foreground/80 text-center sm:text-left">
              Direct download for Windows. Installs in seconds.
            </p>
          )}
        </section>

        {/* FEATURE GRID ----------------------------------------------- */}
        <section aria-label="What you get">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <FeatureCard
              icon={Target}
              title="Frame Data"
              body="Move properties and frame data for every character."
            />
            <FeatureCard
              icon={Gamepad2}
              title="Combo Practice"
              body="On-screen combo trainer to drill routes until they're muscle memory."
            />
            <FeatureCard
              icon={Swords}
              title="Matchup Tools"
              body="Strengths, weaknesses, and key win conditions for each fight."
            />
            <FeatureCard
              icon={Trophy}
              title="Tier Lists & Leaderboards"
              body="Stay ahead of the evolving meta and chase the top players."
            />
          </div>
        </section>

        {/* ABOUT THE APP ---------------------------------------------- */}
        <section
          aria-label="About Mainstage"
          className="rounded-2xl border border-border/60 bg-card/70 backdrop-blur-sm shadow-sm p-5 sm:p-7 space-y-5"
        >
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold">
              Your Best Play with the {activeGameName} Companion App
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-2">
              Focus on improving while the app delivers clear, reliable tools
              that support you{' '}
              <span className="font-medium text-foreground/90">
                before, during, and after every match
              </span>
              . Train efficiently, study characters, track your growth, and
              build your own personal codex of knowledge.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="text-base sm:text-lg font-semibold">
              Character and Gameplay Insights
            </h3>
            <ul className="space-y-2 text-sm sm:text-base">
              <SparkleItem>
                <strong className="font-semibold text-foreground">
                  Frame data
                </strong>{' '}
                and move properties for every character
              </SparkleItem>
              <SparkleItem>
                <strong className="font-semibold text-foreground">
                  Matchup insights
                </strong>{' '}
                highlighting strengths, weaknesses, and key win conditions
              </SparkleItem>
              <SparkleItem>
                <strong className="font-semibold text-foreground">
                  Tier lists
                </strong>{' '}
                that reflect the evolving meta and guide character selection
              </SparkleItem>
              <SparkleItem>
                <strong className="font-semibold text-foreground">
                  Leaderboards
                </strong>{' '}
                to follow top players and compare progress
              </SparkleItem>
            </ul>
          </div>

          <div className="space-y-3">
            <h3 className="text-base sm:text-lg font-semibold">
              Combo Practice & Personal Codex
            </h3>
            <ul className="space-y-2 text-sm sm:text-base">
              <SparkleItem>
                <strong className="font-semibold text-foreground">
                  On-screen combo practice
                </strong>{' '}
                so you can drill routes without leaving the game
              </SparkleItem>
              <SparkleItem>
                <strong className="font-semibold text-foreground">
                  Notes &amp; codex
                </strong>{' '}
                — capture what you learn and revisit it on every device
              </SparkleItem>
            </ul>
          </div>

          {/* In-flow secondary CTA so a reader who scrolled past the hero
              doesn't have to scroll back up to grab the install. */}
          <div className="pt-2">
            <Button
              asChild
              size="lg"
              className="h-12 px-8 text-base font-semibold w-full sm:w-auto"
            >
              <a
                href={download.href}
                rel="noopener noreferrer"
                target={download.isMobile ? '_blank' : undefined}
              >
                <Download className="h-5 w-5" />
                {download.label}
              </a>
            </Button>
          </div>
        </section>

        {/* NEWS ------------------------------------------------------- */}
        <section aria-label="Mainstage news">
          <NewsCarousel items={newsItems} isLoading={newsLoading} />
        </section>

        {/* ABOUT THE CREATOR ----------------------------------------- */}
        <section
          aria-label="About the creator"
          className="rounded-2xl border border-border/60 bg-card/70 backdrop-blur-sm shadow-sm p-5 sm:p-7 space-y-3"
        >
          <h2 className="text-lg sm:text-xl font-semibold">Mainstage Labs</h2>
          <p className="text-sm sm:text-base text-muted-foreground">
            Mainstage is powered by{' '}
            <span className="font-medium text-foreground/90">FlyQuest</span>, a
            tier-one esports organization with a strong history of elevating
            competitive communities. Our first official tool is built as a
            long-term commitment to help players improve, explore the game,
            and stay connected — a reliable place for newcomers and veterans
            alike to learn and grow.
          </p>
        </section>
      </div>
    </div>
  );
}

/**
 * Small icon + title + body tile used in the feature grid. Kept local
 * to this route because it's only used here — promoting it to a shared
 * component would just add an indirection without callers to justify it.
 */
function FeatureCard({
  icon: Icon,
  title,
  body,
}: {
  icon: typeof Target;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/70 backdrop-blur-sm p-4 sm:p-5 space-y-2 hover:border-primary/40 transition-colors">
      <div className="h-9 w-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-sm sm:text-base font-semibold">{title}</h3>
      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
        {body}
      </p>
    </div>
  );
}

/**
 * Bulleted line styled like the store page's sparkle bullets — small
 * accent-colored Sparkles glyph followed by the content.
 */
function SparkleItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5 text-muted-foreground">
      <Sparkles className="h-4 w-4 mt-0.5 text-primary shrink-0" />
      <span>{children}</span>
    </li>
  );
}
